import styled from '@emotion/styled';
import { rem } from '@utils';

const MenuItemInner = styled.div`
  color: var(--menu-item-color);
  column-gap: ${rem(8)};
  cursor: pointer;
  display: flex;
  align-items: center;
  height: ${rem(40)};
  --transition: 200ms ease;
  transition: var(--transition);
  padding: 0 ${rem(22)};
  position: relative;
  user-select: none;

  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    width: 3px;
    height: 100%;
  }

  .dropdown-menu {
    position: absolute;
    inset: 0;
    height: 100%;
    opacity: 0;
  }

  .icon {
    flex-shrink: 0;
  }

  &:focus-visible {
    outline-offset: -2px;
    outline: 1px solid ${({ theme }) => theme.palette.common.white};
  }
`;

const MenuItemIcon = styled.div`
  width: ${rem(16)};
  height: ${rem(16)};
  pointer-events: none;
`;

const MenuItemText = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  text-transform: capitalize;
`;

const GroupList = styled.ol`
  flex: 1;
  overflow: hidden auto;
  touch-action: manipulation;
`;

const GroupListItem = styled.li`
  list-style-type: none;
`;

const Menu = styled.menu<{ isOpen?: boolean }>(
  ({ isOpen }) => `
  display: flex;
  flex-direction: column;
  row-gap: ${rem(16)};
  flex: 1;
  margin: 0 -${rem(isOpen ? 16 : 14)};
  overflow: hidden;

  ${MenuItemInner} .icon {
    transition: flex-basis ease 0ms 200ms;
    pointer-events: none;
  }

  ${MenuItemText} {
    display: ${isOpen ? 'flex' : 'none'};
    flex: ${isOpen ? 1 : 0} 0 auto;
    align-items: center;
    column-gap: 8px;
  }

  ${GroupList} {
    display: ${isOpen ? 'block' : 'none'};
  }
  
  @media (max-width: 1024px) {
    margin: 0 -${rem(16)};
  }
`
);

const CollapseIconWrapper = styled.span`
  display: inline-flex;
  opacity: 0;
  transition: var(--transition);

  ${MenuItemInner}:hover & {
    opacity: 1;
  }
`;

const MenuItem = styled.li<{
  active: boolean;
  hidden?: boolean;
  highlight: boolean;
}>(
  ({ active, highlight, hidden, theme }) => `
  display: ${hidden ? 'none' : 'flex'};
  flex-flow: column;
  list-style-type: none;
  min-height: ${rem(40)};
  overflow: hidden;
    
    ${GroupList} {
      max-height: ${active ? '100%' : 0};
      opacity: ${active ? 1 : 0};
      transition: 
        opacity 150ms ease-in-out ${active ? 150 : 0}ms, 
        max-height 0ms ${active ? 0 : 150}ms;
    }

    ${MenuItemInner} {
      --highlight-color: var(--menu-item-highlight-color);
      --highlight-hover-color: ${
        theme.palette.primary[highlight ? 'dark' : 'main']
      };

      &::before {
        background: ${highlight ? theme.palette.primary.dark : 'transparent'};
      }

      &:not(:hover) {
        color: var(--highlight-color);

        .path-stroke {
          stroke: var(--highlight-color);
        }
      
        .path-fill {
          fill: var(--highlight-color);
        }
      }

      &:hover {
        color: var(--highlight-hover-color);

        .path-stroke {
          stroke: var(--highlight-hover-color);
        }
      
        .path-fill {
          fill: var(--highlight-hover-color);
        }
      }  

      .path-stroke, .path-fill {
        transition: var(--transition);
      }
    }

    ${CollapseIconWrapper} {
      transform: rotateZ(${active ? 180 : 0}deg);
      ${
        active &&
        `
        opacity: 1;

        .path-fill {
          fill: ${theme.palette.primary.dark};
        }
      `
      };
    }
  }
`
);

const GroupTitle = styled.div`
  color: ${({ theme }) => theme.palette.grey['600']};
  font-size: ${rem(12)};
  font-weight: 600;
  line-height: ${rem(16)};
  padding: ${rem(16)} ${rem(24)} ${rem(8)};
`;

const Group = styled.ol();

const GroupItem = styled.li<{ active?: boolean }>(
  ({ active, theme }) => `
  color: ${active ? theme.palette.primary.dark : 'inherit'};
  cursor: pointer;
  display: flex;
  align-items: center;
  height: ${rem(40)};
  font-size: ${rem(14)};
  font-weight: 500;
  opacity: 1;
  padding-top: ${rem(12)};
  padding-bottom: ${rem(12)};
  padding-left: ${rem(24)};
  padding-right: 0;
  position: relative;
  user-select: none;

  &::before {
    background: ${active ? theme.palette.primary.dark : 'transparent'};
    content: '';
    position: absolute;
    inset: 0;
    width: 3px;
    height: 100%;
  }

  &::after {
    background: linear-gradient(
      to right, transparent 82%, var(--sidebar-background-color) 92%
    );
    content: '';
    position: absolute;
    right: 0px;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  
  &:not(:focus-visible, :focus-within, :hover) {
    transition: 
      opacity 200ms linear,
      padding-top 200ms ease-in-out 200ms,
      padding-bottom 200ms ease-in-out 200ms,
      padding-right 0ms 200ms,
      height 200ms ease-in-out 200ms,
      transform 0ms 200ms;
  }

  &.active {
    color: ${theme.palette.primary[active ? 'dark' : 'main']};
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    ${GroupList}:not(.disable-active) &:active {
      background: var(--menu-group-item-highlight-background-color);
      color: ${theme.palette.primary[active ? 'dark' : 'main']};
      transition: 0ms 25ms;
      
      &::after {
        display: none;
      }
    }
  }

  // Only on non-touch devices
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: var(--menu-group-item-highlight-background-color);
      color: ${theme.palette.primary[active ? 'dark' : 'main']};
      transition: none;

      &::after {
        background: linear-gradient(
          to right, transparent 68%, var(--menu-group-item-highlight-background-color) 80%
        );
      }
    }  
  }

  &:focus-within {
    transition: 
      opacity 200ms linear,
      padding-right 0ms ease-in-out 200ms,
      height 200ms ease-in-out 200ms,
      transform 0ms 200ms;
  }

  &:is(:hover, .active, :focus-within) {
    padding-right: ${rem(24)};
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    padding-right: ${rem(24)};

    &::after {
      background: linear-gradient(
        to right, 
        transparent 68%, 
        var(--sidebar-background-color) 80%
      );    
    }    

    &.active {
      background: var(--menu-group-item-highlight-background-color);
    }

    &.active::after {
      background: linear-gradient(
        to right, 
        transparent 68%, 
        var(--menu-group-item-highlight-background-color) 80%
      );
    }
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.primary.dark};
    outline-offset: -2px;
  }

  &.deleted {
    height: 0;
    opacity: 0;
    padding: 0 ${rem(24)};
    pointer-events: none;
    transform: scale(0);
  }
`
);

const GroupItemLink = styled.a`
  position: absolute;
  inset: 0;
`;

const GroupItemText = styled.span`
  flex: 1 0 100%;
  overflow: hidden;
  white-space: nowrap;

  ${GroupItem}.active & {
    // flex: 1 1 auto;
  }

  // Only on non-touch devices
  @media (hover: hover) and (pointer: fine) {
    ${GroupItem}:is(:hover, :has(*.action-menu-active)) & {
      flex: 1 1 auto;
    }
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    ${GroupItem} & {
      flex: 1 1 auto;
    }
  }
`;

const ActionMenuWrapper = styled.div`
  padding-left: ${rem(8)};
  z-index: 1;    

  .action-menu {
    background: transparent;
    color: var(--dropdown-menu-icon-color);
    transform: rotateZ(90deg);
    visibility: hidden;

    svg {
      fill: var(--dropdown-menu-icon-color);
    }

    &:hover {
      background: ${({ theme }) => theme.palette.primary.main};

      svg {
        fill: ${({ theme }) => theme.palette.common.white};
      }
    }
  }

  // Only on non-touch devices
  @media (hover: hover) and (pointer: fine) {
    ${GroupItem}:hover & .action-menu {
      visibility: visible;
    }    
  }

  // On mobile devices
  @media (hover: none) and (pointer: coarse) {
    ${GroupItem} & .action-menu {
      visibility: visible;
    }
  }
  
  .action-menu-active {
    background: ${({ theme }) => theme.palette.primary.dark};
    visibility: visible;
    
    svg {
      fill: ${({ theme }) => theme.palette.common.white};
    }
  }
`;

const Loading = styled.div<{ hidden?: boolean }>`
  display: flex;
  justify-content: center;
  padding: ${rem(16)} 0 ${rem(24)};
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
`;

export default {
  ActionMenuWrapper,
  CollapseIconWrapper,
  Group,
  GroupItem,
  GroupItemLink,
  GroupItemText,
  GroupList,
  GroupListItem,
  GroupTitle,
  Loading,
  Menu,
  MenuItem,
  MenuItemInner,
  MenuItemIcon,
  MenuItemText
};
